'use client';

import { useState } from 'react';
import Image from 'next/image';

interface BoltBadgeProps {
  variant?: 'white-circle' | 'black-circle' | 'text-only';
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
  className?: string;
}

export default function BoltBadge({ 
  variant = 'black-circle', 
  position = 'bottom-right',
  className = '' 
}: BoltBadgeProps) {
  const [isHovered, setIsHovered] = useState(false);

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'bottom-right': 'bottom-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-left': 'bottom-4 left-4'
  };

  const baseClasses = `
    fixed ${positionClasses[position]} z-50 
    transition-all duration-300 transform 
    hover:scale-105 cursor-pointer
    ${className}
  `;

  const renderCircleBadge = () => (
    <div
      className={`
        ${baseClasses}
        w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16
        drop-shadow-lg
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Use official black circle badge */}
      <Image
        src="/black_circle_360x360.png"
        alt="Built with Bolt.new"
        width={64}
        height={64}
        className="w-full h-full object-contain"
        priority
      />
      
      {isHovered && (
        <div className={`
          absolute ${position.includes('top') ? 'top-full' : 'bottom-full'} 
          ${position.includes('right') ? 'right-0' : 'left-0'}
          mt-2 mb-2 px-2 py-1 text-xs 
          bg-black text-white
          rounded whitespace-nowrap shadow-lg
        `}>
          Built with Bolt.new
        </div>
      )}
    </div>
  );

  const renderWhiteCircleBadge = () => (
    <div
      className={`
        ${baseClasses}
        w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16
        bg-white rounded-full p-2 shadow-lg
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Use official black circle badge on white background */}
      <Image
        src="/black_circle_360x360.png"
        alt="Built with Bolt.new"
        width={64}
        height={64}
        className="w-full h-full object-contain"
        priority
      />
      
      {isHovered && (
        <div className={`
          absolute ${position.includes('top') ? 'top-full' : 'bottom-full'} 
          ${position.includes('right') ? 'right-0' : 'left-0'}
          mt-2 mb-2 px-2 py-1 text-xs 
          bg-white text-black border border-gray-200
          rounded whitespace-nowrap shadow-lg
        `}>
          Built with Bolt.new
        </div>
      )}
    </div>
  );

  const renderTextBadge = () => (
    <div
      className={`
        ${baseClasses}
        bg-black/90 backdrop-blur-sm
        px-2 py-1 rounded-lg shadow-lg
        h-8 sm:h-10 md:h-12
        flex items-center
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Use official text logo */}
      <Image
        src="/logotext_poweredby_360w.png"
        alt="Powered by Bolt.new"
        width={120}
        height={30}
        className="h-full w-auto object-contain"
        priority
      />
    </div>
  );

  const handleClick = () => {
    window.open('https://bolt.new/', '_blank', 'noopener,noreferrer');
  };

  return (
    <a
      href="https://bolt.new/"
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        handleClick();
      }}
      className="no-underline block"
      title="Built with Bolt.new"
    >
      {variant === 'white-circle' && renderWhiteCircleBadge()}
      {variant === 'black-circle' && renderCircleBadge()}
      {variant === 'text-only' && renderTextBadge()}
    </a>
  );
} 